import { Component, DestroyRef, inject, OnInit, SecurityContext, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';
import { MatDialog } from '@angular/material/dialog';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { ActivatedRoute } from '@angular/router';
import { TestManagementService } from '../test-management.service';
import {
  AssistantKey,
  ContentType,
  InvocationContextApp,
  InvocationContextAuthState,
  InvocationContextChannel,
  TestResult,
  TestRun,
} from '@vendasta/protos/ai_assistants/v1alpha1';
import { AssistantKeyResolverService } from '../../../core/services/assistant-key-resolver.service';
import { forkJoin } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AvailableModelsService } from '../available-models.service';
import {
  formatCallDuration,
  formatModelAndReasoningFromTestResult,
  getAppName,
  getAuthStateName,
  getChannelName,
  isVoiceTest,
  StatusMap,
} from '../test-run.utils';

import {
  TestToolCallModalComponent,
  TestToolCallModalData,
} from '../test-tool-call-modal/test-tool-call-modal.component';
import { MarkdownService, SECURITY_CONTEXT } from 'ngx-markdown';
import { TranslateService } from '@ngx-translate/core';
import { AiAssistantI18nModule } from '../../../assets/i18n/ai-assistant-i18n.module';
import { TestChatHistoryComponent } from '../test-chat-history/test-chat-history.component';

interface ComparedTestCase {
  testCaseId: string;
  name: string;
  runAResult?: TestResult;
  runBResult?: TestResult;
  scoreDelta?: number;
  statusMatch: boolean;
}

@Component({
  selector: 'ai-test-run-comparison',

  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatExpansionModule,
    MatChipsModule,
    MatTooltipModule,
    MatDividerModule,
    GalaxyPageModule,
    GalaxyLoadingSpinnerModule,
    TestChatHistoryComponent,
    AiAssistantI18nModule,
  ],
  templateUrl: './test-run-comparison.component.html',
  styleUrl: './test-run-comparison.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    MarkdownService,
    { provide: SECURITY_CONTEXT, useValue: SecurityContext.HTML },
    AssistantKeyResolverService,
  ],
})
export class TestRunComparisonComponent implements OnInit {
  private readonly testManagementService = inject(TestManagementService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly availableModelsService = inject(AvailableModelsService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly assistantKeyResolver = inject(AssistantKeyResolverService);
  private readonly translateService = inject(TranslateService);

  readonly models = toSignal(this.availableModelsService.getModelNameLookup());
  protected readonly statusMap = StatusMap;

  // Route params
  runId1: string | null = null;
  runId2: string | null = null;
  assistantKey: AssistantKey | null = null;
  testSuiteId: string | null = null;

  // State
  isLoading = signal<boolean>(true);
  runA = signal<TestRun | null>(null);
  runB = signal<TestRun | null>(null);
  comparedCases = signal<ComparedTestCase[]>([]);
  unmatchedRunA = signal<TestResult[]>([]);
  unmatchedRunB = signal<TestResult[]>([]);

  // Summary stats
  summary = signal<{
    total: number;
    improved: number;
    regressed: number;
    unchanged: number;
    onlyInA: number;
    onlyInB: number;
  }>({ total: 0, improved: 0, regressed: 0, unchanged: 0, onlyInA: 0, onlyInB: 0 });

  ngOnInit(): void {
    this.activatedRoute.paramMap
      .pipe(
        switchMap((paramMap) => {
          this.runId1 = paramMap.get('runId1');
          this.runId2 = paramMap.get('runId2');

          const testSuiteId = paramMap.get('testSuiteId');
          if (testSuiteId) {
            this.testSuiteId = testSuiteId;
            this.assistantKey = null;
            return [null];
          }

          this.testSuiteId = null;
          return this.assistantKeyResolver.resolveAssistantKeyFromParamMap(paramMap);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((resolvedKey) => {
        if (resolvedKey) {
          this.assistantKey = resolvedKey;
        }
        if (this.runId1 && this.runId2 && (this.assistantKey || this.testSuiteId)) {
          this.loadTestRuns();
        }
      });
  }

  private loadTestRuns(): void {
    if (!this.runId1 || !this.runId2) {
      this.isLoading.set(false);
      return;
    }

    const filter: { assistantKey?: AssistantKey; testSuiteId?: string } = {};
    if (this.assistantKey) {
      filter.assistantKey = this.assistantKey;
    } else if (this.testSuiteId) {
      filter.testSuiteId = this.testSuiteId;
    } else {
      this.isLoading.set(false);
      return;
    }

    this.isLoading.set(true);

    forkJoin([
      this.testManagementService.getTestRunV2(this.runId1, filter),
      this.testManagementService.getTestRunV2(this.runId2, filter),
    ])
      .pipe(
        switchMap(([runA, runB]) => {
          this.runA.set(runA);
          this.runB.set(runB);

          return forkJoin([
            this.testManagementService.fetchAllTestResults(this.runId1!, filter),
            this.testManagementService.fetchAllTestResults(this.runId2!, filter),
          ]);
        }),
      )
      .subscribe({
        next: ([resultsA, resultsB]) => {
          this.compareRuns(resultsA, resultsB);
          this.isLoading.set(false);
        },
        error: (err) => {
          console.error('Error loading test runs:', err);
          this.isLoading.set(false);
        },
      });
  }

  private compareRuns(testResultsA: TestResult[], testResultsB: TestResult[]): void {
    const resultsA = new Map<string, TestResult>();
    const resultsB = new Map<string, TestResult>();

    for (const result of testResultsA) {
      resultsA.set(result.id, result);
    }
    for (const result of testResultsB) {
      resultsB.set(result.id, result);
    }

    const comparedCases: ComparedTestCase[] = [];
    const unmatchedA: TestResult[] = [];
    const unmatchedB: TestResult[] = [];
    const allIds = new Set([...resultsA.keys(), ...resultsB.keys()]);

    let improved = 0;
    let regressed = 0;
    let unchanged = 0;

    for (const id of allIds) {
      const resultA = resultsA.get(id);
      const resultB = resultsB.get(id);

      if (resultA && resultB) {
        const scoreA = resultA.score || 0;
        const scoreB = resultB.score || 0;
        const scoreDelta = scoreB - scoreA;
        const statusMatch = resultA.status === resultB.status;

        if (scoreDelta > 0) improved++;
        else if (scoreDelta < 0) regressed++;
        else unchanged++;

        comparedCases.push({
          testCaseId: id,
          name: resultA.name || resultB.name || id,
          runAResult: resultA,
          runBResult: resultB,
          scoreDelta,
          statusMatch,
        });
      } else if (resultA) {
        unmatchedA.push(resultA);
      } else if (resultB) {
        unmatchedB.push(resultB);
      }
    }

    // Sort by name
    comparedCases.sort((a, b) => a.name.localeCompare(b.name));

    this.comparedCases.set(comparedCases);
    this.unmatchedRunA.set(unmatchedA);
    this.unmatchedRunB.set(unmatchedB);

    this.summary.set({
      total: comparedCases.length,
      improved,
      regressed,
      unchanged,
      onlyInA: unmatchedA.length,
      onlyInB: unmatchedB.length,
    });
  }

  getDeltaClass(delta: number | undefined): string {
    if (delta === undefined) return '';
    if (delta > 0) return 'positive';
    if (delta < 0) return 'negative';
    return 'neutral';
  }

  formatDelta(delta: number | undefined): string {
    if (delta === undefined) return '-';
    if (delta > 0) return `+${delta.toFixed(1)}`;
    return delta.toFixed(1);
  }

  formatScore(score: number | undefined): string {
    if (score === undefined) return '-';
    return score.toFixed(1);
  }

  getModelAndReasoning(result: TestResult): string {
    const modelNameLookup = this.models();
    return formatModelAndReasoningFromTestResult(result, modelNameLookup);
  }

  isVoice(result: TestResult): boolean {
    return isVoiceTest(result?.testType);
  }

  getFormattedCallDuration(durationNanos: number): string {
    return formatCallDuration(durationNanos);
  }

  getChannelName(channel: InvocationContextChannel | undefined): string {
    return getChannelName(channel, this.translateService);
  }

  getAuthStateName(authState: InvocationContextAuthState | undefined): string {
    return getAuthStateName(authState, this.translateService);
  }

  getAppName(app: InvocationContextApp | undefined): string {
    return getAppName(app, this.translateService);
  }

  hasAuthState(result: TestResult): boolean {
    const authState = result.invocationContext?.authState;
    return !!authState && authState !== InvocationContextAuthState.UNSET_AUTH_STATE;
  }

  hasApp(result: TestResult): boolean {
    const app = result.invocationContext?.app;
    return !!app && app !== InvocationContextApp.UNSET_APP;
  }

  getContentTypeName(contentType: ContentType): string {
    switch (contentType) {
      case ContentType.REASONING:
        return this.translateService.instant('AI_ASSISTANT.TESTING.CONTENT_TYPE.REASONING');
      case ContentType.ANSWER:
        return this.translateService.instant('AI_ASSISTANT.TESTING.CONTENT_TYPE.ANSWER');
      case ContentType.ACTION_DESCRIPTION:
        return this.translateService.instant('AI_ASSISTANT.TESTING.CONTENT_TYPE.ACTION_DESCRIPTION');
      case ContentType.WORKING:
        return this.translateService.instant('AI_ASSISTANT.TESTING.CONTENT_TYPE.WORKING');
      default:
        return this.translateService.instant('AI_ASSISTANT.TESTING.CONTENT_TYPE.UNSPECIFIED');
    }
  }

  openToolCallCitationDetails(name: string, args: string, response: string): void {
    this.dialog.open(TestToolCallModalComponent, {
      width: '950px',
      maxWidth: '100vw',
      maxHeight: '100vh',
      data: {
        title: name,
        arguments: args,
        response,
      } as TestToolCallModalData,
      autoFocus: false,
    });
  }
}
